import { create } from "zustand";
import type { MenuOption } from "@dealer-center/core-components";
import type { OnSearchInterface } from "./top-bar/search/SearchBar";
import type {
  NavigationMenuItemInterface,
  UserMenuItemsInterface
} from "@dealer-center/ddc-api-commoncontainer-interface";
export const useNavBarStore = create<GetStoreState>(set => {
  return {
    linksMenu: [],
    user: {
      serviceAccount: {}
    },
    nav: [],
    hasBridgeBar: true,
    setVisibility(state) {
      set(newState => {
        return {
          visibility: {
            ...newState.visibility,
            ...state
          }
        };
      });
    },
    visibility: {
      pinLinksMenu: false,
      shouldShowMobileMenu: false,
      shouldSearchBeAnIcon: false,
      shouldShowDealerCenterText: false,
      shouldAccountSelectorBeAnIcon: false,
      shouldShowAccountSelectorInCenter: false,
      showFooter: true,
      shouldNavigationBeAnIcon: false
    },
    setInitalState: (state: SetStoreState) =>
      set({
        linksMenu: state.linksMenu,
        user: state.user,
        nav: state.nav || [],
        hasBridgeBar:
          state.hasBridgeBar === undefined ? true : state.hasBridgeBar,
        availableAccounts: state.availableAccounts,
        selectedAccountId: state.selectedAccountId,
        selectedAccountName: state.selectedAccountName,
        selectedAccountStatus: state.selectedAccountStatus,
        onSearch: state.onSearch,
        setSelectedAccountId: (accountId: string) => {
          if (state.setSelectedAccountId) {
            state.setSelectedAccountId(accountId);
          }
          set({
            selectedAccountId: accountId,
            selectedAccountName: getMenuLabel(
              state.availableAccounts,
              accountId
            )
          });
        }
      }),
    availableAccounts: [],
    selectedAccountId: "",
    selectedAccountName: "",
    selectedAccountStatus: "",
    // Re-derive the name with the id: `AccountSelector` shows the name ahead of
    // the id, so leaving the previous account's name in place would label the
    // newly selected account with it.
    setSelectedAccountId: (account: string) => {
      set(state => ({
        selectedAccountId: account,
        selectedAccountName: getMenuLabel(state.availableAccounts, account)
      }));
    }
  };
});

// The label the account menu shows for an account; the menu falls back to the
// id for an account whose name has not resolved.
function getMenuLabel(
  availableAccounts: MenuOption[] | undefined,
  accountId: string
): string | undefined {
  return availableAccounts?.find(account => account.value === accountId)?.label;
}

interface GetStoreState extends SetStoreState {
  setInitalState: (state: SetStoreState) => void;
  setVisibility: (state: Partial<VisibilityState>) => void;
  visibility: VisibilityState;
  hasBridgeBar: boolean;
  nav: NavigationMenuItemInterface[];
}

export interface VisibilityState {
  pinLinksMenu: boolean;
  shouldShowAccountSelectorInCenter: boolean;
  shouldShowMobileMenu: boolean;
  shouldSearchBeAnIcon: boolean;
  shouldShowDealerCenterText: boolean;
  shouldAccountSelectorBeAnIcon: boolean;
  showFooter: boolean;
  shouldNavigationBeAnIcon: boolean;
}

interface SetStoreState {
  onSearch?: (value: OnSearchInterface) => void;
  linksMenu?: NavigationMenuItemInterface[];
  user?: UserMenuItemsInterface;
  nav?: NavigationMenuItemInterface[];
  hasBridgeBar?: boolean;
  availableAccounts?: MenuOption[];
  selectedAccountId?: string;
  /**
   * Display name of the selected account. Supplied on its own rather than read
   * from `availableAccounts`, because the account being viewed is not always in
   * the menu (e.g. on a deep link that never went through the account history).
   */
  selectedAccountName?: string;
  selectedAccountStatus?: string;
  setSelectedAccountId?: (account: string) => void;
}
